import { Injectable } from '@angular/core';
import { AnalyticsService } from '@app/shared/services/analytics/analytics.service';
import { CookieConsentPopupComponent } from '@app/shared/services/cookie-consent-popup/component/cookie-consent-popup.component';
import { ICookieConsentPopupResult } from '@app/shared/services/cookie-consent-popup/cookie-consent-popup-result';
import { CookieConsentStatusEnum } from '@app/shared/services/cookie-consent-popup/cookie-consent-status.enum';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { Subject, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentPopupService {

  private readonly cookieConsentName = 'CookieConsentStatus';
  private readonly highExpiryDays = 730;
  private readonly lowExpiryDays = 90;
  private consentStatus: CookieConsentStatusEnum;

  constructor(
    private cookieService: CookieService,
    private analyticsService: AnalyticsService,
    private modalService: BsModalService) {
    this.updateConsentStatus();
  }

  initialize(): void {
    switch (this.consentStatus) {
      case CookieConsentStatusEnum.Allow:
        this.analyticsService.loadGoogleAnalytics();
        break;

      case CookieConsentStatusEnum.Disallow:
        this.analyticsService.unloadGoogleAnalytics();
        break;

      default:
        this.askForConsent();
        break;
    }
  }

  revokeCookieConsentPopup(): void {
    this.askForConsent();
  }

  private askForConsent(): void {
    this.openCookieConsentPopup()
      .pipe(
        tap((result: ICookieConsentPopupResult) => {
          if (result.googleAnalyticsEnabled) {
            this.analyticsService.loadGoogleAnalytics();
            this.cookieService.set(this.cookieConsentName, 'Allow', this.highExpiryDays, '/', null, true, 'Strict');
            return;
          }
          this.analyticsService.unloadGoogleAnalytics();
          this.cookieService.set(this.cookieConsentName, 'Disallow', this.lowExpiryDays, '/', null, true, 'Strict');
        }),
        tap(() => this.updateConsentStatus())
      )
      .subscribe();
  }

  private openCookieConsentPopup(): Observable<ICookieConsentPopupResult> {
    const subject = new Subject<ICookieConsentPopupResult>();
    this.modalService.show(CookieConsentPopupComponent,
      {
        keyboard: false,
        class: 'modal-dialog-centered borderless',
        ignoreBackdropClick: true,
        initialState: { subject }
      });
    return subject.asObservable();
  }

  private updateConsentStatus(): void {
    const cookieConsentSettingsExists = this.cookieService.check(this.cookieConsentName);
    if (!cookieConsentSettingsExists) {
      this.consentStatus = CookieConsentStatusEnum.NotSpecified;
      return;
    }

    const consent = this.cookieService.get(this.cookieConsentName);
    switch (consent) {
      case 'Allow':
        this.consentStatus = CookieConsentStatusEnum.Allow;
        break;

      case 'Disallow':
        this.consentStatus = CookieConsentStatusEnum.Disallow;
        break;

      default:
        this.consentStatus = CookieConsentStatusEnum.NotSpecified;
        break;
    }
  }

}
